import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  useEffect,
  useId,
  useRef,
  useState,
  type ComponentPropsWithRef,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react'

import { FOCUS_OUTLINE_ON_WRAPPER } from './focusRing.ts'

import type { InputSize } from './Input'

interface SelectCommonProps {
  size?: InputSize
  required?: boolean
  label?: ReactNode
  helperText?: ReactNode
  errorMessage?: ReactNode
  leadingIcon?: IconDefinition
  trailingIcon?: IconDefinition
  prefixAdornment?: ReactNode
  suffixAdornment?: ReactNode
  trailingAction?: ReactNode
  containerClassName?: string
  fieldClassName?: string
}

export interface NativeSelectProps
  extends SelectCommonProps, Omit<ComponentPropsWithRef<'select'>, 'size'> {
  customDropdown?: never
}

export interface CustomSelectContext {
  close: () => void
}

export interface CustomSelectProps
  extends
    SelectCommonProps,
    Omit<ComponentPropsWithRef<'button'>, 'children' | 'prefix' | 'type' | 'value'> {
  customDropdown: ReactNode | ((context: CustomSelectContext) => ReactNode)
  dropdownClassName?: string
  displayValue?: ReactNode
  placeholder?: ReactNode
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
}

export type SelectProps = NativeSelectProps | CustomSelectProps

const SIZE_CLASSES: Record<InputSize, string> = {
  small: 'h-8',
  medium: 'h-10',
}

function joinClasses(...classes: Array<string | undefined>): string {
  return classes.filter(Boolean).join(' ')
}

function isCustomSelect(props: SelectProps): props is CustomSelectProps {
  return props.customDropdown !== undefined
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null): void {
  if (typeof ref === 'function') {
    ref(value)
  } else if (ref) {
    ref.current = value
  }
}

interface SelectLabelProps {
  inputId: string
  label?: ReactNode
  required: boolean
}

function SelectLabel({ inputId, label, required }: SelectLabelProps) {
  return label ? (
    <label htmlFor={inputId} className="text-tP flex items-start gap-1 text-text-strong">
      <span>{label}</span>
      {required ? (
        <span aria-hidden="true" className="text-text-danger">
          *
        </span>
      ) : null}
    </label>
  ) : null
}

interface SelectDescriptionProps {
  // Explicitly `| undefined` rather than optional: this repo runs
  // exactOptionalPropertyTypes, where "may be absent" and "may be undefined"
  // are different claims and a caller spreading an absent id fails the first.
  id: string | undefined
  description: ReactNode | undefined
  invalid: boolean
}

function SelectDescription({ id, description, invalid }: SelectDescriptionProps) {
  return description ? (
    <p
      id={id}
      className={joinClasses('text-tSmall w-full', invalid ? 'text-text-danger' : 'text-text-weak')}
    >
      {description}
    </p>
  ) : null
}

interface AdornmentProps {
  slot: string
  children: ReactNode
  className: string
}

function Adornment({ slot, children, className }: AdornmentProps) {
  return (
    <span aria-hidden="true" data-slot={slot} className={className}>
      {children}
    </span>
  )
}

function NativeSelect({
  label,
  helperText,
  errorMessage,
  leadingIcon,
  trailingIcon,
  prefixAdornment,
  suffixAdornment,
  trailingAction,
  containerClassName,
  fieldClassName,
  className,
  id,
  size = 'medium',
  disabled = false,
  required = false,
  'aria-describedby': providedDescribedBy,
  'aria-invalid': providedInvalid,
  ref,
  children,
  ...rest
}: NativeSelectProps) {
  const generatedId = useId()
  const selectId = id ?? generatedId
  const description = errorMessage ?? helperText
  const descriptionId = description ? `${selectId}-description` : undefined
  const describedBy = [providedDescribedBy, descriptionId].filter(Boolean).join(' ') || undefined
  const invalid =
    errorMessage !== undefined ||
    (providedInvalid !== undefined && providedInvalid !== false && providedInvalid !== 'false')
  const ariaInvalid = errorMessage !== undefined ? true : providedInvalid
  const status = disabled ? 'disabled' : invalid ? 'error' : 'default'
  const contentColor = disabled ? 'text-text-disabled' : 'text-text-weak'
  const iconColor = disabled ? 'text-icon-disabled' : 'text-icon-neutral'

  return (
    <div className={joinClasses('flex w-full flex-col items-start gap-1', containerClassName)}>
      <SelectLabel inputId={selectId} label={label} required={required} />

      <div
        data-status={status}
        data-size={size}
        className={joinClasses(
          'flex w-full items-center gap-2 rounded-[var(--radius-base)] bg-fill-white px-3 transition-[background-color,border-color] duration-150',
          SIZE_CLASSES[size],
          disabled
            ? 'cursor-not-allowed border border-stroke-disabled'
            : invalid
              ? 'border-2 border-stroke-error-strong hover:bg-fill-hover has-[select:active]:bg-fill-press'
              : 'border border-stroke-strong hover:bg-fill-hover focus-within:border-stroke-selected has-[select:active]:bg-fill-press',
          fieldClassName,
        )}
      >
        {leadingIcon ? (
          <Adornment
            slot="select-leading-icon"
            className={joinClasses('inline-flex shrink-0 items-center justify-center', iconColor)}
          >
            <FontAwesomeIcon icon={leadingIcon} className="size-4" />
          </Adornment>
        ) : null}

        {prefixAdornment ? (
          <Adornment slot="select-prefix" className={joinClasses('text-tP shrink-0', contentColor)}>
            {prefixAdornment}
          </Adornment>
        ) : null}

        <select
          {...rest}
          ref={ref}
          id={selectId}
          disabled={disabled}
          required={required}
          aria-describedby={describedBy}
          aria-invalid={ariaInvalid}
          className={joinClasses(
            // Sinal de foco na borda do INVÓLUCRO (`focus-within:`); aqui só o
            // supressor, e o que preserva o alto contraste do Windows.
            `text-tP min-w-0 flex-1 cursor-pointer appearance-none bg-transparent text-text-strong ${FOCUS_OUTLINE_ON_WRAPPER} disabled:cursor-not-allowed disabled:text-text-disabled`,
            className,
          )}
        >
          {children}
        </select>

        {suffixAdornment ? (
          <Adornment slot="select-suffix" className={joinClasses('text-tP shrink-0', contentColor)}>
            {suffixAdornment}
          </Adornment>
        ) : null}

        {trailingIcon ? (
          <Adornment
            slot="select-trailing-icon"
            className={joinClasses('inline-flex shrink-0 items-center justify-center', iconColor)}
          >
            <FontAwesomeIcon icon={trailingIcon} className="size-4" />
          </Adornment>
        ) : null}

        {trailingAction ? (
          <span
            data-slot="select-trailing-action"
            className={joinClasses('inline-flex shrink-0 items-center [&_svg]:size-4', iconColor)}
          >
            {trailingAction}
          </span>
        ) : null}

        <Adornment
          slot="select-chevron"
          className={joinClasses(
            'pointer-events-none inline-flex shrink-0 items-center justify-center',
            iconColor,
          )}
        >
          <FontAwesomeIcon icon={faChevronDown} className="size-4" />
        </Adornment>
      </div>

      <SelectDescription id={descriptionId} description={description} invalid={invalid} />
    </div>
  )
}

function CustomSelect({
  customDropdown,
  dropdownClassName,
  displayValue,
  placeholder = 'Select an option',
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  label,
  helperText,
  errorMessage,
  leadingIcon,
  trailingIcon,
  prefixAdornment,
  suffixAdornment,
  trailingAction,
  containerClassName,
  fieldClassName,
  className,
  id,
  size = 'medium',
  disabled = false,
  required = false,
  'aria-describedby': providedDescribedBy,
  'aria-invalid': providedInvalid,
  'aria-haspopup': providedHasPopup,
  'aria-controls': providedControls,
  ref,
  onClick,
  onKeyDown,
  ...rest
}: CustomSelectProps) {
  const generatedId = useId()
  const selectId = id ?? generatedId
  const dropdownId = providedControls ?? `${selectId}-dropdown`
  const description = errorMessage ?? helperText
  const descriptionId = description ? `${selectId}-description` : undefined
  const describedBy = [providedDescribedBy, descriptionId].filter(Boolean).join(' ') || undefined
  const invalid =
    errorMessage !== undefined ||
    (providedInvalid !== undefined && providedInvalid !== false && providedInvalid !== 'false')
  const ariaInvalid = errorMessage !== undefined ? true : providedInvalid
  const status = disabled ? 'disabled' : invalid ? 'error' : 'default'
  const contentColor = disabled ? 'text-text-disabled' : 'text-text-weak'
  const iconColor = disabled ? 'text-icon-disabled' : 'text-icon-neutral'
  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const isOpen = controlledOpen ?? internalOpen
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  function setOpen(nextOpen: boolean): void {
    if (controlledOpen === undefined) {
      setInternalOpen(nextOpen)
    }
    onOpenChange?.(nextOpen)
  }

  function close(): void {
    setOpen(false)
    triggerRef.current?.focus()
  }

  useEffect(() => {
    if (!isOpen) return

    function handlePointerDown(event: PointerEvent): void {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [isOpen])

  const renderedDropdown =
    typeof customDropdown === 'function' ? customDropdown({ close }) : customDropdown

  return (
    <div
      ref={containerRef}
      className={joinClasses('flex w-full flex-col items-start gap-1', containerClassName)}
    >
      <SelectLabel inputId={selectId} label={label} required={required} />

      <div className="relative w-full">
        <div
          data-status={status}
          data-size={size}
          className={joinClasses(
            'flex w-full items-center gap-2 rounded-[var(--radius-base)] bg-fill-white px-3 transition-[background-color,border-color] duration-150',
            SIZE_CLASSES[size],
            disabled
              ? 'cursor-not-allowed border border-stroke-disabled'
              : invalid
                ? 'cursor-pointer border-2 border-stroke-error-strong hover:bg-fill-hover has-[button:active]:bg-fill-press'
                : 'cursor-pointer border border-stroke-strong hover:bg-fill-hover focus-within:border-stroke-selected has-[button:active]:bg-fill-press',
            fieldClassName,
          )}
          onClick={() => {
            if (!disabled) {
              setOpen(!isOpen)
              triggerRef.current?.focus()
            }
          }}
        >
          {leadingIcon ? (
            <Adornment
              slot="select-leading-icon"
              className={joinClasses('inline-flex shrink-0 items-center justify-center', iconColor)}
            >
              <FontAwesomeIcon icon={leadingIcon} className="size-4" />
            </Adornment>
          ) : null}

          {prefixAdornment ? (
            <Adornment
              slot="select-prefix"
              className={joinClasses('text-tP shrink-0', contentColor)}
            >
              {prefixAdornment}
            </Adornment>
          ) : null}

          <button
            {...rest}
            ref={(node) => {
              triggerRef.current = node
              assignRef(ref, node)
            }}
            id={selectId}
            type="button"
            disabled={disabled}
            aria-describedby={describedBy}
            aria-invalid={ariaInvalid}
            aria-haspopup={providedHasPopup ?? 'listbox'}
            aria-controls={dropdownId}
            aria-expanded={isOpen}
            className={joinClasses(
              // Idem: a borda do invólucro é o sinal; aqui só o supressor.
              `text-tP min-w-0 flex-1 cursor-pointer truncate bg-transparent text-left text-text-strong ${FOCUS_OUTLINE_ON_WRAPPER} disabled:cursor-not-allowed disabled:text-text-disabled`,
              displayValue === undefined ? 'text-text-weak' : undefined,
              className,
            )}
            onClick={(event) => {
              event.stopPropagation()
              setOpen(!isOpen)
              onClick?.(event)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape' && isOpen) {
                event.preventDefault()
                close()
              }
              onKeyDown?.(event)
            }}
          >
            {displayValue ?? placeholder}
          </button>

          {suffixAdornment ? (
            <Adornment
              slot="select-suffix"
              className={joinClasses('text-tP shrink-0', contentColor)}
            >
              {suffixAdornment}
            </Adornment>
          ) : null}

          {trailingIcon ? (
            <Adornment
              slot="select-trailing-icon"
              className={joinClasses('inline-flex shrink-0 items-center justify-center', iconColor)}
            >
              <FontAwesomeIcon icon={trailingIcon} className="size-4" />
            </Adornment>
          ) : null}

          {trailingAction ? (
            <span
              data-slot="select-trailing-action"
              className={joinClasses('inline-flex shrink-0 items-center [&_svg]:size-4', iconColor)}
              onClick={(event) => event.stopPropagation()}
            >
              {trailingAction}
            </span>
          ) : null}

          <Adornment
            slot="select-chevron"
            className={joinClasses(
              'pointer-events-none inline-flex shrink-0 items-center justify-center',
              iconColor,
            )}
          >
            <FontAwesomeIcon icon={faChevronDown} className="size-4" />
          </Adornment>
        </div>

        {isOpen ? (
          <div
            id={dropdownId}
            data-slot="select-dropdown"
            className={joinClasses('absolute top-full z-50 mt-1 w-full', dropdownClassName)}
          >
            {renderedDropdown}
          </div>
        ) : null}
      </div>

      <SelectDescription id={descriptionId} description={description} invalid={invalid} />
    </div>
  )
}

/**
 * Quave Run select with Input-compatible adornments and native or custom dropdown modes.
 */
export function Select(props: SelectProps): ReactElement {
  return isCustomSelect(props) ? <CustomSelect {...props} /> : <NativeSelect {...props} />
}
